import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { createPropertyActions } from './create-property.actions';
import { CreatePropertyService } from './create-property.service';
import { selectData, selectStepIndex } from './create-property.selectors';
import { LandlordPropertyDetailService } from '../../landlord-property-detail/data/landlord-property-detail.service';
import { MessageService } from '@letly/ui-common';
import { PropertyStatus, ToastSeverity } from '@letly/models';
import { CreatePropertyModel } from './create-property.reducers';

@Injectable()
export class CreatePropertyEffect {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private createPropertyService = inject(CreatePropertyService);
  private landlordPropertyService = inject(LandlordPropertyDetailService);
  private messageService = inject(MessageService);

  confirmStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.confirmStep),
      withLatestFrom(
        this.store.select(selectData),
        this.store.select(selectStepIndex)
      ),
      switchMap(([_action, data, stepIndex]) => {
        if (stepIndex === 0 && !data.id) {
          const cleanData = this.cleanCreatePropertyData(data);
          return this.createPropertyService.createProperty(cleanData).pipe(
            map((response) => {
              if (response.success) {
                return createPropertyActions.onCreateProperty({
                  ...response.data,
                  status: 'success',
                  message: 'Property created.',
                  stepIndex: 1
                });
              }
              this.messageService.showSnackbar(
                response.niceErrorMessage,
                ToastSeverity.Error
              );
              return createPropertyActions.onCreateProperty({
                ...data,
                status: 'error',
                message: response.niceErrorMessage,
                stepIndex: stepIndex
              });
            })
          );
        }

        if (data.id) {
          const cleanData = this.cleanCreatePropertyData(data);
          return this.createPropertyService.updateProperty(cleanData).pipe(
            switchMap((response) => {
              if (response.success) {
                return of(
                  createPropertyActions.onUpdateProperty({
                    ...response.data,
                    status: 'success',
                    message: 'Property updated.',
                    stepIndex: stepIndex
                  })
                );
              }
              this.messageService.showSnackbar(
                response.niceErrorMessage,
                ToastSeverity.Error
              );
              return of(
                createPropertyActions.onUpdateProperty({
                  ...data,
                  status: 'error',
                  message: response.niceErrorMessage,
                  stepIndex: data.stepIndex
                })
              );
            })
          );
        }

        return of(createPropertyActions.nextStep());
      })
    )
  );


  confirmGuaranteed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.confirmGuaranteed),
      withLatestFrom(this.store.select(selectData)),
      switchMap(([_action, data]) => {
        const cleanData = this.cleanCreatePropertyData(data);
        return this.createPropertyService.updateProperty(cleanData).pipe(
          switchMap((response) => {
            if (!response.success) {
              this.messageService.showSnackbar(response.niceErrorMessage, ToastSeverity.Error);
              return of(
                createPropertyActions.onConfirmGuaranteed({
                  ...data,
                  status: 'error',
                  message: response.niceErrorMessage
                })
              );
            }

            return this.createPropertyService.updateStatus(data.id, PropertyStatus.Preparation).pipe(
              map((statusRes) => {
                if (statusRes.success) {
                  return createPropertyActions.onConfirmGuaranteed({
                    ...statusRes.data,
                    status: 'success',
                    message: 'Property updated successfully'
                  });
                }
                this.messageService.showSnackbar(statusRes.niceErrorMessage, ToastSeverity.Error);
                return createPropertyActions.onConfirmGuaranteed({
                  ...response.data,
                  status: 'error',
                  message: statusRes.niceErrorMessage
                });
              })
            );
          })
        );
      })
    )
  );

  goToStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.goToStep),
      withLatestFrom(this.store.select(selectData)),
      switchMap(([action, data]) => {
        if (!data.id) {
          return of(createPropertyActions.onGoToStep({ id: '' }));
        }

        return this.createPropertyService
          .updatePropertyDetail(data.id, { stepIndex: action.stepIndex })
          .pipe(
            map((response) => {
              if (response.success) {
                return createPropertyActions.onGoToStep({
                  id: response.data.id
                });
              }
              this.messageService.showSnackbar(
                response.niceErrorMessage,
                ToastSeverity.Error
              );
              return createPropertyActions.onGoToStep({ id: '' });
            })
          );
      })
    )
  );

  goBack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.previousStep),
      withLatestFrom(this.store.select(selectData)),
      switchMap(([_, data]) => {
        if (!data.id) {
          return of(createPropertyActions.onPreviousStepSuccess());
        }

        return this.createPropertyService.updatePropertyDetail(data.id, { stepIndex: data.stepIndex - 1 }).pipe(
          map((response) => {
            if (response.success) {
              return createPropertyActions.onPreviousStepSuccess();
            }
            this.messageService.showSnackbar(
              response.niceErrorMessage,
              ToastSeverity.Error
            );
            return createPropertyActions.onPreviousStepError({
              message: response.niceErrorMessage
            });
          })
        );
      })
    )
  );

  loadExistingProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.loadExistingProperty),
      switchMap(({ id }) => {
        return this.landlordPropertyService.getPropertyDetail(id).pipe(
          map((response) => {
            if (response.success) {
              return createPropertyActions.onLoadExistingProperty({
                data: {
                  ...response.data,
                  stepIndex:
                    response.data.stepIndex >= 8
                      ? response.data.stepIndex
                      : response.data.stepIndex + 1
                },
                status: 'success',
                message: 'Property loaded.'
              });
            }
            return createPropertyActions.onLoadExistingProperty({
              data: null,
              status: 'error',
              message: response.niceErrorMessage
            });
          })
        );
      })
    )
  );

  loadTiers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.loadTiers),
      withLatestFrom(this.store.select(selectData)),
      switchMap(([_, data]) => {
        if (!data.id) {
          return of(
            createPropertyActions.onLoadTiers({
              tiers: [],
              status: 'error',
              message: 'Property must be created before tiers can be loaded'
            })
          );
        }

        return this.createPropertyService.getPropertyTiers(data.id).pipe(
          map((response) => {
            if (response.success) {
              return createPropertyActions.onLoadTiers({
                tiers: response.data.items ?? [],
                status: 'success',
                message: null
              });
            }
            this.messageService.showSnackbar(
              response.niceErrorMessage,
              ToastSeverity.Error
            );
            return createPropertyActions.onLoadTiers({
              tiers: [],
              status: 'error',
              message: response.niceErrorMessage
            });
          })
        );
      })
    )
  );

  submitConsent = createEffect(() =>
    this.actions$.pipe(
      ofType(createPropertyActions.submitConsent),
      withLatestFrom(this.store.select(selectData)),
      switchMap(([_, data]) => {
        return this.createPropertyService
          .submitConsent(data.id, data.consent)
          .pipe(
            map((response) => {
              if (response.success) {
                return createPropertyActions.onSubmitConsent({
                  data: { ...response.data },
                  status: 'success',
                  message: 'Consent submitted.'
                });
              }
              return createPropertyActions.onLoadExistingProperty({
                data: null,
                status: 'error',
                message: response.niceErrorMessage
              });
            })
          );
      })
    )
  );

  private cleanCreatePropertyData = (data: CreatePropertyModel) => {
    if (Array.isArray(data)) {
      const cleanedArray = data
        .map(this.cleanCreatePropertyData)
        .filter(
          (v) =>
            v !== null &&
            v !== undefined &&
            v !== '' &&
            !(Array.isArray(v) && v.length === 0)
        );

      return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    if (typeof data === 'object' && data !== null) {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(data)) {
        const cleanedValue = this.cleanCreatePropertyData(value);

        const shouldKeep =
          cleanedValue !== null &&
          cleanedValue !== undefined &&
          cleanedValue !== '' &&
          !(Array.isArray(cleanedValue) && cleanedValue.length === 0);

        if (shouldKeep) {
          cleaned[key] = cleanedValue;
        }
      }

      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }

    return data;
  };
}
